import { memo, useCallback, useEffect, useRef, useState, type JSX } from 'react'

import { Switch, Typography } from '@mui/material'
import { MediaAsset } from '@nativewaves/nw-cloud-sdk/media'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Table as ReactTableType, RowSelectionState, noop } from '@tanstack/react-table'
import { pick } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { SelectionToolbar, Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { NewDialog, RowActions, useColumns, SelectionToolbarActions } from 'domains/MediaPlatforms/Assets'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { mediaPlatformAssetsOverviewRoute } from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Assets'
import { mediaPlatformAssetRawRoute } from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Assets/Asset'
import { mediaAssetQueryKeys } from 'services/queryKeys/media'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const tableInstanceRef = useRef<ReactTableType<MediaAsset>>(undefined)

  const navigate = useNavigate({ from: mediaPlatformAssetsOverviewRoute.fullPath })
  const { environmentId, platformId } = mediaPlatformAssetsOverviewRoute.useParams()
  const queryParams = mediaPlatformAssetsOverviewRoute.useSearch()

  const assetsQuery = useInfiniteQuery({
    ...mediaAssetQueryKeys.list({
      environmentId,
      platformId,
      ...queryParams,
    }),
    select: (data) => ({ flat: data.pages.flatMap((page) => page.items) }),
  })
  const assets = assetsQuery.data?.flat

  const columns = useColumns()

  useEffect(() => {
    if (assetsQuery.isSuccess) {
      tableInstanceRef.current?.setRowSelection((old) =>
        Object.keys(old).reduce((acc, newItem) => {
          const newItemFound = assets?.find((asset) => asset.id === newItem)
          if (newItemFound) {
            return { ...acc, [newItem]: true }
          }
          return acc
        }, {} as RowSelectionState),
      )
    }
  }, [assets, assetsQuery.isSuccess])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:MediaPlatform.assets.searchAssets')}
          onSearch={({ search }) => navigate({ search: { ...queryParams, query: search } })}
          defaultValue={queryParams.query}
        />
        <Typography>{t('domain:MediaPlatform.assets.searchByIdOrName')}</Typography>
        <Switch
          checked={queryParams.idOrNameSearch}
          onChange={(_, checked) => navigate({ search: { ...queryParams, idOrNameSearch: checked } })}
          size="small"
        />

        <BasicListOperation
          refreshAction={useCallback(
            () => queryClient.invalidateQueries({ queryKey: mediaAssetQueryKeys.pageList({ environmentId }).queryKey }),

            [environmentId, queryClient],
          )}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(tableInstance) => void (tableInstanceRef.current = tableInstance)}
        size={48}
        data={assets ?? []}
        columns={columns}
        isInitialLoadingGlobal={assetsQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={mediaPlatformAssetsOverviewRoute.fullPath}
            to={mediaPlatformAssetRawRoute.to}
            params={{ assetId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id', 'contentRepositoryId']}
        enableInfinity
        infinityProps={pick(assetsQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        enableVirtualization
        virtualizedProps={{
          getVirtualItems: noop,
        }}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('entity:content.expManifest', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
        renderSelectionToolbar={({ selectedRows }) => (
          <SelectionToolbar
            selectedRows={selectedRows}
            title={t('entity:content.expManifest', { count: 2 })}
            discardSelection={() => tableInstanceRef.current?.resetRowSelection()}
            Actions={
              tableInstanceRef.current && (
                <SelectionToolbarActions selectedRows={selectedRows} tableInstance={tableInstanceRef.current} />
              )
            }
          />
        )}
      />

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
